import { useTranslation } from 'react-i18next'

import { useConfirmationResult } from '../useConfirmationResult'

import { Alert, ConfirmationResultModal, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { InternalService } from '@percent/admin-dashboard/api/actions/internalServices/internalServices.types'

type DeleteServiceModalProps = {
  service: InternalService
  onClose: () => void
  onDeleted: () => void
}

export function DeleteServiceModal({ service, onClose, onDeleted }: Readonly<DeleteServiceModalProps>) {
  const { t } = useTranslation()
  const { internalServiceService } = useServices()
  const [{ isPending }, { mutateAsync: deleteService }] = useMutation(internalServiceService.deleteService)
  const result = useConfirmationResult({ onClose, onSuccess: onDeleted, isBusy: isPending })

  const confirm = () => result.run(() => deleteService({ id: service.id }))

  return (
    <ConfirmationResultModal
      isOpen
      state={result.state}
      title={t('modal.deleteService.title')}
      primaryButtonText={t('button.delete')}
      loadingText={t('modal.deleteService.button.deleting')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={confirm}
      onClose={result.dismiss}
      onFailureClose={result.backToConfirm}
      loading={isPending}
      primaryBtnTestId="delete-service-submit-button"
      secondaryBtnTestId="delete-service-cancel-button"
      buttonTestId="delete-service-result-button"
      success={{
        title: t('modal.deleteService.success.title'),
        description: t('modal.deleteService.success.description', { name: service.name }),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.deleteService.error.title'),
        description: t('modal.deleteService.error.description', { name: service.name }),
        button: t('button.close')
      }}
    >
      <Alert variant="warning" title={t('modal.deleteService.alert')} />
      <Text size="small">{t('modal.deleteService.description', { name: service.name })}</Text>
    </ConfirmationResultModal>
  )
}
